import React, { useCallback } from "react";
import { styled } from "goober";
import { t } from "i18next";

import router from "@/__main__/router.mjs";
import ShareIcon from "@/inline-assets/share.svg";
import globals from "@/util/global-whitelist.mjs";

const DEFAULT_NATIVE_SHARE = true;

const Share = styled("span")`
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  cursor: pointer;
  transition: color var(--transition);

  &:hover {
    color: var(--shade0);
  }
`;

const doShare = (share: boolean, withQuery = false) => {
  const { currentPath, searchParams, metadata } = router.route || {};
  const url = currentPath
    ? `${globals.location.origin}${encodeURI(currentPath)}${withQuery && searchParams ? `?${searchParams.toString()}` : ""}`
    : globals.location.href;
  if (share) {
    globals.navigator.share({
      title: metadata ? t(...metadata.title) : "Blitz",
      text: metadata
        ? t(...metadata.description)
        : "View amazing contents of this link",
      url,
    });
  } else {
    globals.navigator.clipboard.writeText(url);
  }
};

const TOOLTIP_LABEL: Translation = [
  "common:share.copied",
  "Link copied to clipboard!",
];

function ShareButton({
  withText = true,
  useNativeShare = DEFAULT_NATIVE_SHARE,
  withQuery = false,
  ...restProps
}: {
  /**
   * Show "Share" text next to button
   * @default true
   */
  withText?: boolean;
  /**
   * Include current query params in share URL
   * @default false
   */
  withQuery?: boolean;
  /**
   * Use native share API if available
   * @default DEFAULT_NATIVE_SHARE
   */
  useNativeShare?: boolean;
} & React.HTMLAttributes<HTMLSpanElement>) {
  // verify if the browser supports native share
  useNativeShare &&= Boolean(globals.navigator?.share);

  // shows tooltip if action is COPY, since share provides UI feedback
  const tooltipProps = !useNativeShare && {
    "data-tooltip": t(...TOOLTIP_LABEL),
    "data-event": "click",
  };

  const onClick = useCallback(
    () => doShare(useNativeShare, withQuery),
    [useNativeShare, withQuery],
  );

  return (
    <Share
      onClick={onClick}
      role="button"
      tabIndex={0}
      {...tooltipProps}
      {...restProps}
    >
      <ShareIcon width="12" /> {withText && t("common:share.share", "Share")}
    </Share>
  );
}

export default ShareButton;
